/**
 * PersonAccessPanel — the D25 person-centric access view (ADR-113; closes B53).
 *
 * > Staff search for a human being and see **every place that person has
 * > access** — their own account, accounts they help run, consultant
 * > assignments, partner and beta roles, Advocate membership — **each named
 * > properly**, each changeable or removable in place.
 *
 * ⭐ THE SCREEN DECIDES NOTHING. Every judgement — what a row is called, whether
 * it grants account reach, whether this surface owns it — is server-decided and
 * rendered as given. A client that recomputed any of them would be a second
 * implementation of a rule that already exists, free to drift from it. That
 * drift IS B53's shape.
 *
 * ⛔ THE ADVOCATE ROW IS SHOWN AND IS MARKED AS GRANTING NOTHING. D25 lists
 * Advocate membership among the places to show, and D10 says it confers no
 * account reach whatsoever. Both are true at once, so the row appears with an
 * explicit "No account access" marker rather than being silently omitted —
 * omitting it would leave a staff member unable to see that the person earns,
 * and showing it unmarked would imply reach it does not have.
 *
 * Standing directives honoured: all feedback through `useToast()` (never inline,
 * never a raw backend error); a skeleton AND a text status while loading, in
 * tandem; body copy ≥16px and controls ≥17px on an AA-contrast surface.
 */
import { useCallback, useEffect, useState } from 'react';
import { useToast } from '../../components/ToastProvider';
import { friendlyFromUnknown } from '../../lib/friendly-error';

/**
 * ⛔⛔ EVERY ERROR ON THIS PANEL GOES THROUGH `friendlyFromUnknown`, NEVER
 * `toastFromError`.
 *
 * The standing directive is absolute: "a raw API error string or HTTP status code
 * must never reach the user". `toastFromError` does exactly that — it formats the
 * status into the title and the REQUEST PATH plus the RAW JSON BODY into the
 * detail, so a refused assignment would surface as
 * `Something went wrong (HTTP 409)` over
 * `/admin/people/…/consultant-assignments/…  {"error":"client_disassociated"}`.
 *
 * That is not merely ugly here, it is the opposite of what this screen needs:
 * `client_disassociated` means the CUSTOMER closed the relationship and only they
 * can re-open it. A staff member shown a status code will read a system fault and
 * retry; shown the sentence, they know the refusal is correct and whose move it
 * is. `friendlyStaffError` maps the code and its fall-through NEVER returns the
 * raw message.
 */
const toastError = (err: unknown) => ({
  title: 'Could not complete that change',
  detail: friendlyFromUnknown(err),
});
import {
  ACTION_LABELS,
  DESTRUCTIVE_ACTIONS,
  appointOperator,
  assignToSubscriber,
  changeAccessEntry,
  fetchAllPersonAccess,
  removeAccessEntry,
  unassignFromSubscriber,
  KIND_LABELS,
  type AccessAction,
  type AppointPortalType,
  type AccessEntry,
  type AccessEntryKind,
  type PersonAccessView,
} from '../../lib/person-access-api';
import { consoleZoneLabel, useWorkingZone, zoneByCode } from '../../lib/zones';
import { useZoneReadReport } from '../../components/ZoneContext';
import { GraderReinstateModal, GraderRemovalModal } from './GraderRemovalModal';
import { InstantTime } from '../../lib/time-format';
import { ZoneFanoutWarning } from '../../components/ZoneIndicators';
import { ResidencyCountryField, resolveCreateZone } from '../../components/ResidencyCountryField';
import { useZoneMap } from '../../lib/zone-map';
import { zoneFor } from '../../obw/residency';

/**
 * Presentation order for the grouped sections — the ONE thing this screen does
 * decide, and it is deliberately not an authority judgement.
 *
 * The server returns a flat, already-ordered `entries` list; this regroups it by
 * kind for scanning. The sequence mirrors the server's own reasoning in
 * `buildPersonAccessView` — the person's own footprint first, then the powers
 * they hold over other people's accounts, then earning last:
 *
 *   account  →  partner / operator  →  consultant_connection  →  advocate
 *
 * ⚠ `advocate` IS LAST BECAUSE IT GRANTS NOTHING (D10), NOT BECAUSE IT MATTERS
 * LEAST. A staff member scanning top-to-bottom meets every access-granting row
 * before reaching the one that is earning-only, so the section they stop reading
 * at is never one that confers reach.
 *
 * ⛔ A KIND MISSING FROM THIS ARRAY IS SILENTLY DROPPED FROM THE SCREEN — the
 * grouping filters `view.entries` BY this list rather than iterating the entries
 * themselves. Adding a new `AccessEntryKind` server-side without adding it here
 * produces a screen that omits a place the person has access to, with no error
 * and nothing in the payload to hint at it. On the screen that owns revocation,
 * an invisible grant is the worst available failure. If you extend
 * `AccessEntryKind`, extend this array in the same change.
 */
const KIND_ORDER: AccessEntryKind[] = [
  'account',
  'partner',
  'operator',
  'consultant_connection',
  'advocate',
];

interface Props {
  /** The person whose access is being shown. */
  cognitoSub: string;
  /** For the heading — the screen is about a HUMAN, not a subject id. */
  personLabel?: string;
  /**
   * ⭐⭐ EMBEDDED IN THE SUBSCRIBER DRILLDOWN, which already owns the person.
   *
   * D25 says "ONE person-centric screen". A person-centric screen already
   * existed — the User Management drilldown, with memberships, display name,
   * account status, danger zone and the activity trail — and following D25's
   * text literally produced a SECOND one. That is the fragmentation D25 exists
   * to end, so the sections were merged into the drilldown and this component
   * now renders inside it.
   *
   * ⛔ THE TWO WERE NOT DUPLICATES, AND THE DIFFERENCE WAS THE BUG. The
   * drilldown showed only `USER#` rows; this panel also shows `CASSIGN#`
   * consultant connections. A consultant on two subscriber accounts was
   * invisible on the screen that claimed to centralise everything about him —
   * B53, still live, which is what the merge closes.
   *
   * When embedded: the panel drops its own heading and intro (the drilldown
   * supplies both) and hides `kind: 'account'` entries, because the drilldown
   * renders those itself as its editable memberships list. See
   * {@link ACCOUNT_KIND_IS_THE_DRAWERS} for why hiding by KIND — rather than by
   * a second predicate — is what makes the split total.
   */
  embedded?: boolean;
  /** Re-read the drilldown after a mutation, so its Activity trail catches up. */
  onChanged?: () => void;
  /**
   * The person's email — required to APPOINT, and deliberately passed in rather
   * than read from the access view.
   *
   * ⚠ THE SERVER RESOLVES AN APPOINTMENT'S IDENTITY BY EMAIL (ADR-081 — an
   * existing identity is reused, never duplicated on a second attachment), so
   * this is an input to the operation and not decoration. It comes from the
   * drilldown's Cognito-sourced `detail.email`; deriving it from an attachment
   * row instead would fail for exactly the person most likely to need appointing
   * — one with no attachments yet.
   */
  personEmail?: string;
}

/**
 * ⭐⭐ THE SPLIT IS BY `kind`, AND THAT IS WHAT MAKES IT TOTAL.
 *
 * When embedded, the drilldown renders exactly the rows whose `nodeType` is a
 * SUBSCRIBER tier, and this panel renders everything else. Both sides derive
 * from the SAME server-assigned `kind` (`person-access.ts` maps
 * `nodeType: 'partner' → partner`, `'management' → operator`, anything else →
 * `account`), so the two sets are complements by construction.
 *
 * ⛔ DO NOT REPLACE THIS WITH A SECOND PREDICATE ON THE CLIENT. That is exactly
 * how the live defect this merge fixes came about: `isOperatorMembership` in
 * `subscribers-api.ts` tested `nodeType === 'management' || role ∈ operator_*`,
 * while the SERVER's refusal (`subscriber-memberships.ts`) had been widened to
 * "not a subscriber tier". A `partner_admin` on a `partner` node satisfied the
 * server's test and not the client's — so the drilldown rendered it as an
 * editable row whose every save 409'd `operator_membership_readonly`. Two
 * predicates for one boundary is the drift; one `kind` is the fix.
 *
 * An entry hidden here and absent from the drilldown would be an INVISIBLE
 * ATTACHMENT — the worst outcome on the screen that owns revocation — so
 * `user-drilldown-access-merge.test.ts` asserts the partition is total.
 */
const ACCOUNT_KIND_IS_THE_DRAWERS: AccessEntryKind = 'account';

export function PersonAccessPanel({
  cognitoSub, personLabel, embedded, onChanged, personEmail,
}: Props) {
  const toast = useToast();
  const [view, setView] = useState<PersonAccessView | null>(null);
  /**
   * ADR-122 Pattern 5 — zones that did not answer the person-keyed fan-out.
   *
   * ⭐⭐ THIS SCREEN'S CLAIM IS COMPLETENESS ("every place this person has access"), so an
   * unanswered zone is not a shorter list here — it is a false statement about someone's
   * reach, on the screen where access gets revoked. A staff member who revokes everything
   * they can see and is not told a jurisdiction went unasked believes they have finished.
   */
  const [failedZones, setFailedZones] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  /** `${kind}:${id}` of the row mid-action, or null. Per ROW, not a global flag —
   *  a global one would freeze every control on the screen while one row worked. */
  const [busy, setBusy] = useState<string | null>(null);
  /** ADR-133 Amendment 2 §5 — the Grader row's two verbs open their own modals (a reason is required for both). */
  const [graderModal, setGraderModal] = useState<{ mode: 'remove' | 'reinstate'; row: AccessEntry } | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const v = await fetchAllPersonAccess(cognitoSub);
      setView(v);
      setFailedZones(v.failedZones);
    } catch (err) {
      // Standing directive: never inline, never the raw backend string.
      toast.push({ variant: 'error', ...toastError(err) });
      // ⚠ LEFT AS NULL, NOT AS AN EMPTY VIEW. An empty list would read as "this
      // person has access nowhere" — a confident wrong answer on the screen
      // where access gets revoked. Null renders the explicit failure state.
      setView(null);
    } finally {
      setLoading(false);
    }
  }, [cognitoSub, toast]);

  useEffect(() => { void load(); }, [load]);

  // ADR-123 Decision 1 — Pattern 5: the navigation bar names every zone this person's access was
  // gathered from, with the entries each held.
  useZoneReadReport({
    rows: loading ? null : (view?.entries ?? []), failedZones, scope: 'Access',
  });

  /**
   * Run one action, then RE-READ.
   *
   * ⭐⭐ THE RE-READ IS NOT A REFRESH NICETY — IT IS THE ONLY HONEST ANSWER. This
   * screen's whole job is to say where a person still has access, so after a
   * mutation the list must come from the server, never from a local edit. A
   * client-side splice would show the row gone while the row remained (a failed
   * conditional write, a guard that refused, a race with another staffer) — a
   * confident wrong answer on the screen where access gets revoked, which is
   * exactly the failure `load()` refuses to produce on the read path.
   *
   * ⚠ AND IT RE-READS AFTER A FAILURE TOO. A partial success — the attachment
   * gone, the consent grant not flipped — leaves the screen wrong in the other
   * direction if the error path skips the reload.
   */
  const runAction = useCallback(async (row: AccessEntry, action: AccessAction) => {
    // The Grader row's removal and reinstatement need a reason, so they open a modal rather than a confirm.
    if (action === 'remove_grader' || action === 'reinstate_grader') {
      setGraderModal({ mode: action === 'remove_grader' ? 'remove' : 'reinstate', row });
      return;
    }
    if (DESTRUCTIVE_ACTIONS.has(action)) {
      const ok = window.confirm(
        `${ACTION_LABELS[action]} “${row.label}”?\n\n` +
        'This takes effect immediately and cannot be undone from this screen.',
      );
      if (!ok) return;
    }
    setBusy(`${row.kind}:${row.id}`);
    // ADR-122 Pattern 5 — the ROW'S own zone, from the fan-out tag. The account or org this
    // row attaches to lives in exactly one jurisdiction, so a revoke aimed at the wrong one
    // 404s — and on THIS screen a 404 on a revoke reads as "already gone", which is the most
    // dangerous possible misreading: the access remains and the staff member believes it does
    // not.
    const rowZone = zoneByCode(row.zone);
    try {
      if (action === 'unassign') {
        // The server refuses without the org; the read view supplies it per row.
        if (!row.orgId) throw new Error('missing_org_on_row');
        await unassignFromSubscriber(cognitoSub, row.id, row.orgId, rowZone);
      } else if (action === 'remove') {
        await removeAccessEntry(cognitoSub, row, rowZone);
      } else {
        await changeAccessEntry(cognitoSub, row, action, rowZone);
      }
      toast.push({
        variant: 'success',
        title: `${ACTION_LABELS[action]}d`,
        detail: `${row.label} — done.`,
      });
    } catch (err) {
      // Standing directive: shared toast, friendly message, never the raw string.
      toast.push({ variant: 'error', ...toastError(err) });
    } finally {
      setBusy(null);
      await load();
      // ⭐ THE DRILLDOWN RE-READS TOO, and that is not cosmetic: the Activity
      // trail now lives in the SAME modal as these actions, so a mutation whose
      // audit row does not appear beside it would look unrecorded. The audit is
      // written server-side either way; this is what makes it visible without a
      // manual reopen.
      onChanged?.();
    }
  }, [cognitoSub, load, toast, onChanged]);

  if (loading) {
    // Skeleton AND text status, in tandem — the skeleton conveys structure, the
    // text conveys context (standing directive).
    return (
      <div className="pa-panel" aria-busy="true">
        <p style={{ fontSize: 16, color: '#cbd5e1', margin: '0 0 12px' }}>
          Loading everywhere this person has access…
        </p>
        {[0, 1, 2].map((i) => (
          <div
            key={i}
            aria-hidden="true"
            style={{
              height: 52, borderRadius: 10, marginBottom: 8,
              background: 'linear-gradient(90deg,#1e293b 25%,#243449 37%,#1e293b 63%)',
            }}
          />
        ))}
      </div>
    );
  }

  if (!view) {
    return (
      <div className="pa-panel">
        <p style={{ fontSize: 16, color: '#cbd5e1' }}>
          That person’s access could not be loaded. Try again — this list is
          deliberately not shown partially.
        </p>
        <button type="button" onClick={() => void load()} style={btn}>Retry</button>
      </div>
    );
  }

  const visible = embedded
    // The drilldown's own memberships editor owns these — see the ⭐⭐ note on
    // ACCOUNT_KIND_IS_THE_DRAWERS. Hidden by KIND, never by a second predicate.
    ? view.entries.filter((e) => e.kind !== ACCOUNT_KIND_IS_THE_DRAWERS)
    : view.entries;

  /**
   * ⭐ A CONSULTANT WITH NO CUSTOMERS IS A CONSULTANT WITH NO CUSTOMERS (27 September 2026).
   * The programme membership arrives as an `operator` entry carrying `consultantCode`; the
   * client accounts they reach are separate `consultant_connection` rows and may number
   * zero. That zero used to make this screen say the person "holds no … consultant access",
   * in both zones (Dashboard Access findings §7). Now the membership renders as itself and
   * the connections section is kept on screen, empty, saying so — an empty section is a
   * fact about the customer list, not about whether they are a consultant.
   */
  const isConsultant = visible.some((e) => e.kind === 'operator' && !!e.consultantCode);
  const grouped = KIND_ORDER
    .map((kind) => ({ kind, rows: visible.filter((e) => e.kind === kind) }))
    .filter((g) => g.rows.length > 0 || (g.kind === 'consultant_connection' && isConsultant));

  return (
    <div className="pa-panel">
      {/* Embedded, the drilldown already names the person and owns the heading —
          repeating it would put the same words on the screen twice. */}
      {!embedded && (
        <>
          <h3 style={{ fontSize: 20, fontWeight: 800, color: '#f1f5f9', margin: '0 0 4px' }}>
            Access for {personLabel ?? 'this person'}
          </h3>
          <p style={{ fontSize: 16, color: '#cbd5e1', margin: '0 0 18px' }}>
            {/* ⛔ THE SCREEN SAYS WHAT IT DOES. This sentence was withdrawn earlier
                in the D25 write-path build, when it was D25's INTENT stated as
                as-built and the server exposed a single GET; it returns now that the
                write routes are behind it. If a future change removes those routes,
                this sentence comes out with them — an overclaim on a revocation
                screen is worse than silence, because a staff member can believe they
                revoked access they did not. */}
            Every place this person can reach, and everything they hold. This is the
            only screen that changes operator access.
          </p>
        </>
      )}

      <ZoneFanoutWarning failedZones={failedZones} noun="access entries" onRetry={() => void load()} />

      {view.unnamedCount > 0 && (
        // The naming gap is surfaced, not hidden. A staff member seeing
        // "Unnamed partner (…)" should know it is a data problem, not a name.
        <p style={{ fontSize: 14, color: '#fbbf24', margin: '0 0 14px' }}>
          {view.unnamedCount} {view.unnamedCount === 1 ? 'entry' : 'entries'} could not be
          named — the underlying record may be missing.
        </p>
      )}

      {grouped.length === 0 && (
        <p style={{ fontSize: 16, color: '#cbd5e1' }}>
          {embedded
            ? 'This person holds no partner, operator, consultant or Advocate access.'
            : 'This person holds no access anywhere.'}
        </p>
      )}

      {grouped.map((g) => (
        <section key={g.kind} style={{ marginBottom: 20 }}>
          <h4 style={{ fontSize: 16, fontWeight: 700, color: '#94a3b8', margin: '0 0 8px' }}>
            {KIND_LABELS[g.kind]}
          </h4>
          {g.rows.map((row) => (
            <AccessRow
              key={`${row.kind}:${row.id}`}
              row={row}
              busy={busy === `${row.kind}:${row.id}`}
              onAction={(a) => void runAction(row, a)}
            />
          ))}
          {g.kind === 'consultant_connection' && g.rows.length === 0 && (
            <p data-consultant-connections="empty" style={{ fontSize: 16, color: '#cbd5e1', margin: 0 }}>
              No customers assigned yet.
            </p>
          )}
        </section>
      ))}

      <GraderRemovalModal
        open={graderModal?.mode === 'remove'}
        sub={cognitoSub}
        label={personLabel ?? 'this person'}
        zone={graderModal?.row.zone}
        onClose={() => setGraderModal(null)}
        onDone={() => { setGraderModal(null); void load(); onChanged?.(); }}
      />
      <GraderReinstateModal
        open={graderModal?.mode === 'reinstate'}
        sub={cognitoSub}
        label={personLabel ?? 'this person'}
        zone={graderModal?.row.zone}
        removedBecause={graderModal?.row.graderRemoval
          ? [...graderModal.row.graderRemoval.reasonLabels, graderModal.row.graderRemoval.note].filter(Boolean).join('; ')
          : undefined}
        onClose={() => setGraderModal(null)}
        onDone={() => { setGraderModal(null); void load(); onChanged?.(); }}
      />

      <AppointPanel
        cognitoSub={cognitoSub}
        personEmail={personEmail}
        // Only an operator org that confers account reach can have customers assigned through it — a Grader
        // attachment (ADR-133 Amendment 2 §2) is an operator row that reaches no account, so it is never offered.
        consultantOrgs={view.entries.filter((e) => e.kind === 'operator' && e.orgId && e.grantsAccountReach !== false)}
        onDone={async () => { await load(); onChanged?.(); }}
      />
    </div>
  );
}

/**
 * D25's APPOINT half — *"Appointing a consultant and assigning them to
 * subscribers happens here."*
 *
 * ⛔⛔ ONLY THE REMOVAL SIDE SHIPPED. The write-path build gave this screen
 * suspend / reactivate / remove / unassign — every verb that takes access AWAY —
 * and none that grants it. D25 names both halves in one sentence, so a screen
 * with only the destructive half still sends a staff member to Dashboard Access to
 * finish the job, which is the two-tools problem B53 is.
 *
 * ⚠ ADVOCATE IS ABSENT FROM THE PICKER AND MUST STAY ABSENT. Advocates
 * self-register through the AOW; there is no admin-creatable path and there must
 * not be one. Its omission here is a decision, not an oversight — see the
 * assertion in `user-drilldown-access-merge.test.ts`.
 */
function AppointPanel({
  cognitoSub, personEmail, consultantOrgs, onDone,
}: {
  cognitoSub: string;
  personEmail?: string;
  consultantOrgs: AccessEntry[];
  onDone: () => Promise<void>;
}) {
  const toast = useToast();
  const [open, setOpen] = useState<null | 'appoint' | 'assign'>(null);
  const [busy, setBusy] = useState(false);
  const [portalType, setPortalType] = useState<AppointPortalType>('consultant');
  const [orgId, setOrgId] = useState('');
  const [role, setRole] = useState('operator_admin');
  /**
   * ADR-122 finding #4 — the zone a NEWLY-MINTED operator org is born in.
   *
   * ⚠ IT APPLIES ONLY WHEN NO ORG IS NAMED. Appointing into an EXISTING org must follow that
   * org's own zone — the attachment row lands in the org's partition, so a different zone
   * would 404 on the org. `newOrgZone` is therefore consulted only on the mint path below, and
   * the control is hidden when an org id is supplied. Two zone answers on one form, where only
   * one of them is ever a choice, is how an operator ends up overriding a resolved zone with a
   * guessed one.
   */
  // ⭐ ADR-124 D3/D4 — a NEW appointment's zone follows the appointee's OWN country (a consultant
  // can only serve the zone they were created in), through the served map. It replaces the free
  // "which zone" choice ADR-122 finding #4 added here.
  const [country, setCountry] = useState('');
  const zoneMap = useZoneMap();
  const workingZone = useWorkingZone();
  const derivedCountryZone = country && zoneMap ? zoneFor(zoneMap, country) : null;
  const newOrgZone = resolveCreateZone(derivedCountryZone?.zone);
  const [clientAccountId, setClientAccountId] = useState('');
  const [assignOrgId, setAssignOrgId] = useState(consultantOrgs[0]?.orgId ?? '');

  const run = async (fn: () => Promise<void>, ok: { title: string; detail: string }) => {
    setBusy(true);
    try {
      await fn();
      toast.push({ variant: 'success', ...ok });
      setOpen(null);
    } catch (err) {
      // ⭐ THE REFUSAL IS THE POINT, NOT AN EDGE CASE. `client_disassociated`
      // means the CUSTOMER closed this relationship — writing the assignment
      // anyway would create a row that looks like access and reaches nothing.
      // It must arrive as a sentence a staff member can act on, never as a code.
      toast.push({ variant: 'error', ...toastError(err) });
    } finally {
      setBusy(false);
      await onDone();
    }
  };

  return (
    <section style={{ marginTop: 24, paddingTop: 18, borderTop: '1px solid #334155' }}>
      <h4 style={{ fontSize: 16, fontWeight: 700, color: '#94a3b8', margin: '0 0 10px' }}>
        Appoint
      </h4>

      {open === null && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          <button type="button" style={btn} onClick={() => setOpen('appoint')}>
            + Appoint to an operator org
          </button>
          <button
            type="button"
            style={{ ...btn, opacity: consultantOrgs.length ? 1 : 0.45,
                     cursor: consultantOrgs.length ? 'pointer' : 'not-allowed' }}
            disabled={consultantOrgs.length === 0}
            // ⚠ DISABLED WITH A REASON, not hidden. A staff member who expects to
            // assign a subscriber needs to know WHY they cannot — "they are not a
            // consultant yet" is actionable; a missing button is not.
            title={consultantOrgs.length
              ? 'Assign this consultant to a subscriber account'
              : 'This person holds no operator programme attachment — appoint them first'}
            onClick={() => setOpen('assign')}
          >
            + Assign to a subscriber
          </button>
        </div>
      )}

      {open === 'appoint' && (
        <div style={formRow}>
          <select
            aria-label="Operator org type" style={field} value={portalType}
            onChange={(e) => {
              const v = e.target.value as AppointPortalType;
              setPortalType(v);
              setRole(v === 'partner' ? 'partner_admin' : 'operator_admin');
            }}
          >
            {/* ⛔ NO ADVOCATE OPTION — see the ⚠ note on this component. */}
            <option value="consultant">Consultant programme</option>
            <option value="beta">Beta programme</option>
            <option value="partner">Partner organisation</option>
          </select>
          {portalType === 'partner' && (
            <input
              style={field} placeholder="Partner org id" aria-label="Partner org id"
              value={orgId} onChange={(e) => setOrgId(e.target.value)}
            />
          )}
          <select aria-label="Role" style={field} value={role} onChange={(e) => setRole(e.target.value)}>
            {(portalType === 'partner'
              ? ['partner_admin', 'partner_staff', 'partner_view']
              : ['operator_admin', 'operator_staff']
            ).map((r) => <option key={r} value={r}>{r}</option>)}
          </select>
          {/* Only when a new org will be MINTED — an existing org's zone is resolved, never
              chosen. `orgId` is only collected for `partner`, so every other portalType is
              always a mint. */}
          {!(portalType === 'partner' && orgId.trim()) && (
            <ResidencyCountryField
              id="appoint-country"
              value={country}
              onChange={setCountry}
              map={zoneMap}
              workingZone={workingZone}
              disabled={busy}
            />
          )}
          <button
            type="button" style={btn}
            // ADR-124 — a new appointment needs its zone, i.e. the appointee's country.
            disabled={busy || !personEmail || (!(portalType === 'partner' && orgId.trim()) && !newOrgZone)}
            // ⚠ THE SERVER RESOLVES THE IDENTITY BY EMAIL (ADR-081 — never a
            // second identity on a second attachment), so the email is required.
            // Without it the appointment would land on nobody; refusing here is
            // clearer than a 400 from a field the staff member cannot see.
            title={personEmail ? undefined : 'This person has no email on record — appointment needs one'}
            onClick={() => void run(
              // ⭐ TWO DIFFERENT QUESTIONS, ANSWERED IN THE RIGHT ORDER.
              //   · An EXISTING org: its zone is a FACT, resolved from the org row. The
              //     attachment lands in that org's partition, so any other zone 404s.
              //   · A NEW appointment: its zone FOLLOWS the appointee's own country (ADR-124
              //     D3/D4) — not a choice, and never the console's own zone by default.
              () => {
                const named = consultantOrgs.find((o) => o.orgId === orgId.trim());
                return appointOperator(cognitoSub, {
                  portalType, role, email: personEmail!,
                  ...(portalType === 'partner' ? { orgId: orgId.trim() } : {}),
                  ...(country ? { jurisdiction: country } : {}),
                }, named ? zoneByCode(named.zone) : newOrgZone ?? undefined);
              },
              {
                title: 'Appointed',
                detail: `${personEmail} → ${portalType}${
                  ` in ${consoleZoneLabel(
                    consultantOrgs.find((o) => o.orgId === orgId.trim())?.zone ?? newOrgZone?.code ?? '',
                  )}`
                }.`,
              },
            )}
          >
            {busy ? 'Working…' : 'Appoint'}
          </button>
          <button type="button" style={btn} disabled={busy} onClick={() => setOpen(null)}>Cancel</button>
        </div>
      )}

      {open === 'assign' && (
        <div style={formRow}>
          <select
            aria-label="Operator programme" style={field} value={assignOrgId}
            onChange={(e) => setAssignOrgId(e.target.value)}
          >
            {consultantOrgs.map((o) => (
              <option key={o.orgId} value={o.orgId}>{o.label}</option>
            ))}
          </select>
          <input
            style={field} placeholder="Subscriber account id" aria-label="Subscriber account id"
            value={clientAccountId} onChange={(e) => setClientAccountId(e.target.value)}
          />
          <button
            type="button" style={btn} disabled={busy || !clientAccountId.trim() || !assignOrgId}
            onClick={() => void run(
              // The ORG's zone: the CASSIGN# row lands in the client account's partition, and
              // an operator org and its clients are in the same jurisdiction by construction.
              () => assignToSubscriber(cognitoSub, clientAccountId.trim(), assignOrgId,
                zoneByCode(consultantOrgs.find((o) => o.orgId === assignOrgId)?.zone)),
              { title: 'Assigned', detail: `Assigned to ${clientAccountId.trim()}.` },
            )}
          >
            {busy ? 'Working…' : 'Assign'}
          </button>
          <button type="button" style={btn} disabled={busy} onClick={() => setOpen(null)}>Cancel</button>
        </div>
      )}
    </section>
  );
}

const formRow: React.CSSProperties = {
  display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'stretch',
};
// Shared height + border-box + horizontal-only padding, so the controls in the
// row share a baseline by construction (the standing alignment directive).
const field: React.CSSProperties = {
  height: 40, boxSizing: 'border-box', padding: '0 10px', fontSize: 16,
  background: '#1e293b', color: '#f1f5f9',
  border: '1px solid #475569', borderRadius: 10,
};

/**
 * One place.
 *
 * ⭐ THE ROW IS A FLEX GROUP WITH A SHARED HEIGHT AND `align-items: stretch`, so
 * its controls share a baseline BY CONSTRUCTION rather than by two numbers being
 * kept equal by hand — the pattern the alignment directive prescribes after the
 * PL2/PL26 battles. It is measured by
 * `scripts/probe-adr-113-person-access-row.mjs`, per shipping variant.
 *
 * ⛔⛔ THE ROW RENDERS THE SERVER'S ACTIONS AND INVENTS NONE. `availableActions` is
 * mapped straight to buttons: no filtering by kind, no reading `status` to choose
 * between Suspend and Reactivate, no local notion of what "should" be editable.
 * A `Manage` button once stood here with no handler at all, and before that the
 * row decided its own editability from a boolean — both are versions of the screen
 * holding an opinion about authority. An EMPTY list renders as no buttons, which
 * is the honest rendering of "this screen does not own this row"; it is never a
 * disabled control, because a dead control reads as broken software.
 */
function AccessRow({
  row, busy, onAction,
}: {
  row: AccessEntry;
  busy: boolean;
  onAction: (action: AccessAction) => void;
}) {
  return (
    <div
      data-access-row={row.kind}
      style={{
        display: 'flex', alignItems: 'stretch', gap: 8, marginBottom: 8,
      }}
    >
      <div
        style={{
          flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 10,
          padding: '12px 14px', boxSizing: 'border-box',
          background: '#1e293b', border: '1px solid #334155', borderRadius: 10,
        }}
      >
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{
            display: 'block', fontSize: 16, fontWeight: 700,
            // An unresolved name is dimmed and italic so it reads as a gap even
            // before the warning line above is noticed.
            color: row.nameResolved ? '#f1f5f9' : '#94a3b8',
            fontStyle: row.nameResolved ? 'normal' : 'italic',
            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
          }}>
            {row.label}
          </span>
          {row.detail && (
            <span style={{ display: 'block', fontSize: 14, color: '#cbd5e1' }}>{row.detail}</span>
          )}
          {/* ADR-133 Amendment 2 §5 — a removed grader's row says why, so Reinstate is decided with the reason in view. */}
          {row.graderRemoval && (
            <span data-grader-removed-because style={{ display: 'block', fontSize: 14, color: '#fecaca' }}>
              Removed <InstantTime iso={row.graderRemoval.removedAt} mode="date" empty="" />
              {' — '}
              {[...row.graderRemoval.reasonLabels, row.graderRemoval.note].filter(Boolean).join('; ')}
            </span>
          )}
          {/* ADR-122 Pattern 5 — the ZONE, on the row rather than in a column.
              ⭐ THIS SCREEN'S ROWS ARE CARDS GROUPED BY KIND, NOT A TABLE, so "zone-labelled"
              means a label ON the row; a column would have nowhere to live. It sits with the
              detail line because that is where a staff member already reads what the row IS,
              and on a revocation screen "which jurisdiction am I revoking in" belongs beside
              the name, not in a filter somewhere above. */}
          {row.zone && (
            <span
              data-access-zone={row.zone}
              style={{ display: 'block', fontSize: 14, color: '#94a3b8' }}
            >
              {consoleZoneLabel(row.zone)}
            </span>
          )}
        </span>

        {/* ⛔ D10, stated on the row itself. Server-decided; never inferred here. */}
        {!row.grantsAccountReach && (
          <span
            title={row.kind === 'advocate'
              ? 'Earning only — this confers no access to any subscriber account'
              : 'This confers no access to any subscriber account'}
            data-access-badge="no-account-reach"
            style={{
              // ⛔ 14px IS THE FLOOR, AND THIS PILL IS THE WORST PLACE TO BREAK IT.
              // It was 12px — below the accessibility minimum for any visible text —
              // on a SECURITY-RELEVANT label: "NO ACCOUNT ACCESS" is the one marker
              // that stops an Advocate row reading as reach. A marker a reader
              // squints past is worse than no marker, because the row still looks
              // authoritative.
              flexShrink: 0, fontSize: 14, fontWeight: 800, letterSpacing: '0.04em',
              // Shared height + inline-flex + border-box + HORIZONTAL-ONLY padding —
              // the alignment directive's prescribed construction, so the badge sits
              // on the same baseline as the text beside it instead of two paddings
              // being kept equal by hand. Measured, per variant, by
              // `scripts/probe-adr-113-person-access-row.mjs`.
              height: 26, display: 'inline-flex', alignItems: 'center',
              boxSizing: 'border-box', padding: '0 10px', borderRadius: 999,
              background: '#334155', color: '#cbd5e1',
            }}
          >
            NO ACCOUNT ACCESS
          </span>
        )}
      </div>

      {/* ⭐ ONE BUTTON PER SERVER-PUBLISHED ACTION. Every control in this row —
          including the NO ACCOUNT ACCESS pill above — carries the SAME height and
          `box-sizing: border-box` with horizontal-only padding, so the group shares
          a baseline by construction rather than by two numbers being kept equal by
          hand (the standing alignment directive; measured per variant by
          `scripts/probe-adr-113-person-access-row.mjs`, not eyeballed). */}
      {row.availableActions.map((action) => (
        <button
          key={action}
          type="button"
          data-access-action={action}
          disabled={busy}
          onClick={() => onAction(action)}
          title={
            action === 'remove_grader' ? 'Remove the Grader role, with a reason — they cannot re-join by training'
            : action === 'reinstate_grader' ? 'Reinstate the Grader role, with a reason'
            : action === 'remove' ? 'Remove this access entirely'
            : action === 'unassign' ? 'Remove this consultant from this subscriber'
            : action === 'suspend' ? 'Suspend this attachment — the door disappears, the identity is untouched'
            : 'Restore this attachment'
          }
          style={{
            ...btn,
            flexShrink: 0,
            opacity: busy ? 0.5 : 1,
            cursor: busy ? 'progress' : 'pointer',
            // The irreversible verbs read as such before they are clicked, not
            // only in the confirm dialog.
            ...(DESTRUCTIVE_ACTIONS.has(action)
              ? { color: '#fecaca', borderColor: '#7f1d1d' }
              : {}),
          }}
        >
          {ACTION_LABELS[action]}
        </button>
      ))}
    </div>
  );
}

const btn: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  padding: '0 16px', boxSizing: 'border-box',
  fontSize: 17, fontWeight: 700,
  background: '#334155', color: '#f1f5f9',
  border: '1px solid #475569', borderRadius: 10,
};
